import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Project} from '../../../models/project';
import {StatItem} from '../../../models/stats/stat-item';
import {StatHandler} from '../../../classes/data/stat-handler';
import {Audience} from '../../../models/audience';
import {ProjectCrawlStatus} from '../../../models/crawl-status';
import {Crawl} from '../../../models/crawl';
import {environment} from '../../../../environments/environment';
import {MapDataHandler} from '../../../classes/data/map-data-handler';
import {MapData} from '../../../models/stats/map-data';
import {MapGeoData, MapLevel} from '../../sp-chart/chart.service';
import {TreeNode} from '../../../classes/data/tree-node';
import {PathEntry} from '../../../classes/data/path-entry';
import {firstValueFrom} from 'rxjs';
import {Folder} from '../../../models/folder';

@Injectable({
  providedIn: 'root'
})
export class ApiDataService {
  private scalaApiUrl = environment.config.api.statsUrl;
  private coreApiUrl = environment.config.api.baseUrl;
  private diApiUrl = environment.config.api.diBaseUrl;

  constructor(private httpClient: HttpClient) { }

  getMapJson(country: string, version: number): Promise<MapGeoData> {
    return new Promise<MapGeoData>((resolve, reject) => {
      const url = `${this.coreApiUrl}maps/${country}/v/${version}`;
      this.httpClient.get<any>(url).subscribe(mapData => {
        if (version < 2) {
          const children = mapData.children;

          const regions = mapData.regions ? mapData.regions : undefined;
          const provinces = mapData.provinces ? mapData.provinces : undefined;
          const districts = mapData.districts ? mapData.districts : undefined;
          const cities = mapData.cities ? mapData.cities : undefined;

          let levelCount = 0;
          if (regions) levelCount = 1;
          if (provinces) levelCount = 2;
          if (districts) levelCount = 3;
          if (cities) levelCount = 4;

          const geo = new MapGeoData(children, levelCount, 1);

          if (regions) geo.setLevel(MapLevel.LEVEL_1, regions);
          if (provinces) geo.setLevel(MapLevel.LEVEL_2, provinces);
          if (districts) geo.setLevel(MapLevel.LEVEL_3, districts);
          if (cities) geo.setLevel(MapLevel.LEVEL_4, cities);

          resolve(geo);
        } else {
          const level1 = mapData.level1 ? mapData.level1 : undefined;
          const level2 = mapData.level2 ? mapData.level2 : undefined;
          const level3 = mapData.level3 ? mapData.level3 : undefined;
          const level4 = mapData.level4 ? mapData.level4 : undefined;

          let levelCount = 0;
          if (level1) levelCount = 1;
          if (level2) levelCount = 2;
          if (level3) levelCount = 3;
          if (level4) levelCount = 4;

          const geo = new MapGeoData(undefined, levelCount, 2);

          if (level1) geo.setLevel(MapLevel.LEVEL_1, level1);
          if (level2) geo.setLevel(MapLevel.LEVEL_2, level2);
          if (level3) geo.setLevel(MapLevel.LEVEL_3, level3);
          if (level4) geo.setLevel(MapLevel.LEVEL_4, level4);

          resolve(geo);
        }
      }, error => reject(error));
    });
  }

  getMapData(project: Project, version: number): Promise<MapDataHandler> {
    return new Promise<MapDataHandler>((resolve, reject) => {
      this.httpClient.get<MapData[]>(`${this.scalaApiUrl}/map_data?target=${project.audience_target.id}&benchmark=${project.audience_bench.id}&version=${version}`)
        .subscribe(data => {
          resolve(new MapDataHandler(data));
        }, error => reject(error))
    });
  }

  getDataExcel(project: Project, normalized: boolean = true): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.httpClient.get<any>(`${this.scalaApiUrl}/audiences/excel/${normalized ? 'normalized' : 'raw'}/${project.audience_target.id}/${project.audience_bench.id}?useNewTags=true`, { responseType: 'blob' as 'json'})
        .subscribe(excel => {
          resolve(excel)
        }, error => reject(error))
    });
  }

  getDataCSV(project: Project, normalized: boolean = true): Promise<any> {
    return firstValueFrom(this.httpClient.get<any>(`${this.scalaApiUrl}/audiences/csv/${normalized ? 'normalized' : 'raw'}/${project.audience_target.id}/${project.audience_bench.id}?useNewTags=true`, { responseType: 'blob' as 'json'}));
  }

  getFolderNormalizedDataCSV(folder: Folder): Promise<any> {
    return firstValueFrom(this.httpClient.get<any>(`${this.scalaApiUrl}/audiences/batch/csv/normalized/${folder.id}?useNewTags=true`));
  }

  getCrawledCriterion(target: Audience, benchmark: Audience, normalized: boolean = true, seed?: number): Promise<StatHandler> {
    return new Promise<StatHandler>((resolve, reject) => {
      this.httpClient.get<Array<StatItem|any>>(`${this.scalaApiUrl}/audiences/${normalized ? 'normalized' : 'raw'}/${target.id}/${benchmark.id}/criterion?${seed ? 'seed=' + seed : ''}`)
        .subscribe(data => {
          data.forEach(d => {
            if (d.type == "tags") {
              d.id = Math.abs(d.id) // Reverse negative ids to positive for compatibility
            }

          });

          const criterionData = data.filter(d => d.type !== "tags" && d.type !== "personae");
          const tagsData = data.filter(d => d.type == "tags");
          const personaeData = data.filter(d => d.type == "personae");

          let statHandler = new StatHandler();

          statHandler.add("tags", tagsData);
          statHandler.add("criterion", criterionData);
          statHandler.add("personae", personaeData);
          resolve(statHandler)
        }, error => reject(error));
    });
  }


  getCrawledCriterionByType(
    audienceId: number,
    benchmarkAudienceId: number,
    criteriaTypes: string[],
    params: {
      normalized: boolean,
      pathsFilter?: string[],
      namesFilter?: string[],
      idsFilter?: number[],
      minPenetration?: number,
      maxPenetration?: number,
      limit?: number,
      sortBy?: string,
      forceRefresh?: boolean
    }
  ): Promise<StatItem[]>{
    return new Promise<StatItem[]>((resolve, reject) => {

      // Build request body for POST
      const requestBody: {
        criteriaTypes: string[],
        normalized: boolean,
        pathsFilter?: string[],
        namesFilter?: string[],
        idsFilter?: number[],
        minPenetration?: number,
        maxPenetration?: number,
        limit?: number,
        sortBy?: string
      } = {
        criteriaTypes: criteriaTypes,
        normalized: params.normalized
      };

      if (params.limit) requestBody.limit = params.limit;
      if (params.sortBy) requestBody.sortBy = params.sortBy;
      if (params.pathsFilter?.length) requestBody.pathsFilter = params.pathsFilter;
      if (params.namesFilter?.length) requestBody.namesFilter = params.namesFilter;
      if (params.idsFilter?.length) requestBody.idsFilter = params.idsFilter;
      if (params.minPenetration) requestBody.minPenetration = params.minPenetration;
      if (params.maxPenetration) requestBody.maxPenetration = params.maxPenetration;
      
      // Use forceRefresh as query param if needed (cache control)
      let urlParams = params.forceRefresh ? '?forceRefresh=true' : '';

      this.httpClient.post<Array<StatItem|any>>(`${this.scalaApiUrl}/audiences/criterion/${audienceId}/${benchmarkAudienceId}${urlParams}`, requestBody)
        .subscribe({
          next: data => {
            data.forEach(d => {
              if (d.type == "tags") {
                d.id = Math.abs(d.id) // Reverse negative ids to positive for compatibility
              }
            });

            resolve(data)
          },
          error: error => reject(error)});
    });
  }

  getSunburstData(project: Project, categoryFilter?: string): Promise<TreeNode<PathEntry>[]> {
    if (!categoryFilter) return this.httpClient.get<TreeNode<PathEntry>[]>(this.scalaApiUrl + "/audiences/sunburst/" + project.audience_target.id).toPromise();
    else return this.httpClient.get<TreeNode<PathEntry>[]>(this.scalaApiUrl + "/audiences/sunburst/" + project.audience_target.id, {
      params: {
        'filter': categoryFilter
      }
    }).toPromise();
  }

  getAudienceLatestCrawl(audience: Audience): Promise<Crawl | null> {
    return firstValueFrom(this.httpClient.get<Audience>(this.coreApiUrl + `audiences/${audience.id}`))
      .then(fetched => fetched?.latest_crawl ?? null);
  }

  getProjectCrawlStatus(projectId: number): Promise<ProjectCrawlStatus> {
    return new Promise<ProjectCrawlStatus>(resolve => {
      this.httpClient
        .get<{ status: ProjectCrawlStatus }>(this.diApiUrl + `v1/meta/projects/${projectId}/crawl-status`)
        .subscribe(response => resolve(response?.status ?? 'unknown'), () => resolve('unknown'));
    });
  }
}
